import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/styles'
import {
    Autocomplete,
    Chip,
    IconButton,
    TextField,
    Tooltip,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Cancel'
import SearchIcon from '@material-ui/icons/Search'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import { matchSorter } from 'match-sorter'

import Theme from '../../assets/theme'
import { useParams } from '../../utils/Hooks'
import { clauseAutocompleteOptions } from '../../utils/autocomplete'
import { Analytics, Keyboard, UrlManipulation } from '../../utils'

const useStyles = makeStyles((theme) => ({
    rootPaper: {
        minWidth: 535,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        backgroundColor: 'white',
    },
    input: {
        minWidth: 500,
        backgroundColor: 'white',
    }
}))

const maxOptions = 5
const filterOptions = (options, { inputValue }) =>
    matchSorter(options, inputValue).slice(0, maxOptions)

const uniqueList = list => [...new Set(list)]

const SearchField = () => {
    const classes = useStyles()
    const { t } = useTranslation()
    const { value } = useParams()
    const [inputValue, setInputValue] = React.useState('')

    const handleSearch = React.useCallback(value => {
        UrlManipulation.goToSearch({
            value: value,
            page: 1,
        })
    }, [])

    const searchValue = value.length > 0 ? value.join(', ') : 'EMPTY SEARCH'
    React.useEffect(() => {
        Analytics.searchEvent(searchValue)
    }, [searchValue])

    return (
        <div className={classes.rootPaper}>
            <Autocomplete
                autoComplete
                open
                fullWidth
                size='small'
                multiple
                freeSolo
                filterSelectedOptions
                clearText=''
                options={inputValue ? uniqueList(clauseAutocompleteOptions) : []}
                filterOptions={filterOptions}
                value={value}
                onChange={(event, newValue) => {
                    handleSearch(newValue)
                }}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue)
                }}
                onKeyPress={Keyboard.onEnter(() => {
                    handleSearch(value)
                })}
                renderInput={inputProps => (
                    <TextField
                        className={classes.input}
                        {...inputProps}
                        variant='outlined'
                        color='primary'
                        placeholder={value.length === 0 ? t('search.keyword') : null}
                    />
                )}
                renderTags={(value, getTagProps) => {
                    return value.map((option, index) => (
                        <Chip
                            variant='outlined'
                            color='primary'
                            label={option}
                            deleteIcon={
                                <CloseIcon fontSize="small" color='primary' />
                            }
                            {...getTagProps({ index })}
                        />
                    ))
                }}
                clearIcon={
                    <CloseIcon fontSize="small" color='primary' />
                }
                renderOption={(props, option, { inputValue }) => {
                    const matches = match(option, inputValue)
                    const parts = parse(option, matches)
                    return (
                        <div {...props}>
                            {parts.map((part, index) => (
                                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400, color: part.highlight ? Theme.palette.primary.dark : 'black', whiteSpace: 'pre' }}>
                                    {part.text}
                                </span>
                            ))
                            }
                        </div >
                    )
                }}
            />
            <Tooltip title={t('tooltips.search')} aria-label="search">
                <div>
                    <IconButton
                        size='small'
                        className={classes.iconButton}
                        onClick={() => {
                            handleSearch(inputValue ? [...value, inputValue] : value)
                        }}
                        onKeyPress={() => {
                            handleSearch(inputValue ? [...value, inputValue] : value)
                        }}
                        aria-label="searchButton"
                    >
                        <SearchIcon />
                    </IconButton>
                </div >
            </Tooltip>
        </div>
    )
}

export default SearchField